import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertTitle,
  Button,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { ApiError, type IEtagged } from '@/domains/api';
import type {
  IRefreshSchedule,
  IRefreshScheduleBody
} from '../../api/brokercheck-refresh-contracts';
import { brokerCheckRefreshApi, refreshRefusal } from '../../api/brokercheck-refresh-api';
import {
  DAYS_OF_WEEK,
  FREQUENCIES,
  WEEKS_OF_MONTH,
  dayOfWeekLabel,
  frequencyLabel,
  weekOfMonthLabel
} from '../../brokercheck-refresh';
import { withUnknown } from '../../components/admin-page';
import { refusalMessage } from '../../refusal';
import { sentence, unanswered } from '../person-hub/said';

/**
 * The BrokerCheck refresh's schedule as a form (SR-222, F10's R3, R4): one form, one Save, the
 * seven members as survey-api's `PUT` takes them, sent under `If-Match` quoted back from the read.
 *
 * **The form judges nothing.** survey-api checks every member and answers 400 with every problem
 * at once, each said beside its member; the binder's own refusal (`$.windowHours` for `abc`) is
 * said the same way. What the form does decide is only what a body *is*: the week of the month is
 * drawn and sent only while the frequency is not weekly — weekly sends `null`, which is what
 * survey-api takes; the window goes as a JSON number where it is plain digits and as typed where
 * not (`1e1` is not 10, and `Number()` would say it is), the routing percent's rule; and a time
 * the browser holds half typed is held with a sentence, never sent as the `''` the time input
 * hands over for it — only the element's `validity.badInput` tells the two apart, the date
 * precedent (`field-controls.tsx`). The time zone's list is the browser's, a help and never a
 * rule: Chromium's lacks `UTC` and `US/Eastern`, which survey-api resolves and saves as spelled.
 *
 * **Enabled is a member, not a switch that writes.** The `PUT` is the whole row, and a toggle that
 * saved itself would send whatever half-edited rest the form held.
 *
 * **A code this app has no word for is kept and drawn as spelled** — a stored frequency, week or
 * day survey-api added — and goes back as it came unless the admin chooses another.
 *
 * The answers are told by status and `type` (`refreshRefusal`), never by `title`: a 400 at the
 * members; 409 `revision-conflict` keeps the draft and offers Reload, which reads afresh and drops
 * it (asked first when anything was changed); a 428 is this page's fault and said so; an answer
 * that never arrived is the page's (`onUnanswered`), which reads again — the form takes the row
 * afresh only if the read brought a different revision, so a save that did not land keeps the
 * typing. A save that lands is the page's too (`onSaved`): the answer and its `ETag` are adopted,
 * not re-read. Rejected: re-reading after a 200 — the answer *is* the row as saved, and a read
 * could only race it.
 *
 * **A new revision is taken in place, never by drawing the form afresh** (gate 2): a form remounted
 * under a `key` took the Save just pressed with it, and the Reload confirmation open over a
 * conflict, and left the focus on the page's body. So the form resets its draft and what was said
 * when the revision it holds changes, and a Reload that lands hands the focus to its first control.
 */

/** The schedule's seven members — the `PUT`'s, and the `[data-schedule-field]` hooks. */
type Member = keyof IRefreshScheduleBody;

const MEMBERS: readonly Member[] = [
  'enabled',
  'frequency',
  'weekOfMonth',
  'dayOfWeek',
  'startLocal',
  'timeZone',
  'windowHours'
];

/** What the form holds — every member as its control holds it. */
interface IDraft {
  enabled: boolean;
  frequency: string;
  /** `''` while none is chosen — a weekly schedule's stored `null`. */
  weekOfMonth: string;
  dayOfWeek: string;
  startLocal: string;
  timeZone: string;
  windowHours: string;
}

function draftOf(schedule: IRefreshSchedule): IDraft {
  return {
    enabled: schedule.enabled,
    frequency: schedule.frequency,
    weekOfMonth: schedule.weekOfMonth ?? '',
    dayOfWeek: schedule.dayOfWeek,
    startLocal: schedule.startLocal,
    timeZone: schedule.timeZone,
    windowHours: String(schedule.windowHours)
  };
}

/** The body a draft sends — see the module's comment for the two decisions in it. */
function bodyOf(draft: IDraft): IRefreshScheduleBody {
  return {
    enabled: draft.enabled,
    frequency: draft.frequency,
    weekOfMonth:
      draft.frequency === 'weekly' || draft.weekOfMonth === '' ? null : draft.weekOfMonth,
    dayOfWeek: draft.dayOfWeek,
    startLocal: draft.startLocal,
    timeZone: draft.timeZone,
    windowHours: /^\d+$/.test(draft.windowHours) ? Number(draft.windowHours) : draft.windowHours
  };
}

interface IProblem {
  target: string;
  reason: string;
}

/** The member a problem names — `windowHours`, or the binder's `$.windowHours` — or `null`. */
function memberOf(target: string): Nullable<Member> {
  const name = target.startsWith('$.') ? target.slice(2) : target;
  return (MEMBERS as readonly string[]).includes(name) ? (name as Member) : null;
}

/** The browser's IANA ids, read once — a help for the time zone's box, never its rule. */
let zoneList: Nullable<string[]> = null;
function browserZones(): string[] {
  if (zoneList === null) {
    const intl = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
    zoneList = typeof intl.supportedValuesOf === 'function' ? intl.supportedValuesOf('timeZone') : [];
  }
  return zoneList;
}

export function ScheduleForm({
  schedule,
  etag,
  onDirty,
  onSaving,
  onSaved,
  onUnanswered,
  onReload
}: {
  schedule: IRefreshSchedule;
  /** The read's `ETag`; `null` turns Save off — a save is never sent without one. */
  etag: Nullable<string>;
  /** Whether the form holds anything not saved — the next slot says it is the saved one's. */
  onDirty: (dirty: boolean) => void;
  /** A save is on its way — what the page said of the last one is no longer the news. */
  onSaving: () => void;
  onSaved: (answer: IEtagged<IRefreshSchedule>) => void;
  onUnanswered: () => void;
  /** Reads the schedule afresh; `true` when it landed, and the form then takes it in place. */
  onReload: () => Promise<boolean>;
}) {
  const id = React.useId();
  const [draft, setDraft] = React.useState<IDraft>(() => draftOf(schedule));
  const [busy, setBusy] = React.useState(false);
  const [problems, setProblems] = React.useState<IProblem[]>([]);
  const [halfTyped, setHalfTyped] = React.useState(false);
  const [conflict, setConflict] = React.useState(false);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  const [confirmingReload, setConfirmingReload] = React.useState(false);
  const [reloading, setReloading] = React.useState(false);
  const timeInput = React.useRef<HTMLInputElement>(null);
  // Set when a Reload lands: the control that had the focus — Reload, inside the conflict's
  // alert or its confirmation — has gone with the conflict, so the focus goes to the form's first.
  const handBack = React.useRef(false);
  // Set when a save has its answer: Save is disabled while the write is out, and Chromium takes
  // the focus off a disabled control, so it is handed back to Save once Save is enabled again.
  const saved = React.useRef(false);

  // The revision held, taken in place when another comes — a save's answer, or a Reload's read.
  const revision = etag ?? `revision-${schedule.revision}`;
  const [held, setHeld] = React.useState(revision);
  if (held !== revision) {
    setHeld(revision);
    setDraft(draftOf(schedule));
    setProblems([]);
    setHalfTyped(false);
    setConflict(false);
    setRefusal(null);
  }

  React.useEffect(() => {
    if (saved.current && !busy) {
      saved.current = false;
      const at = document.activeElement;
      if (at === null || at === document.body) document.getElementById(`${id}-save`)?.focus();
    }
    if (!handBack.current || confirmingReload) return;
    handBack.current = false;
    document.getElementById(`${id}-enabled`)?.focus();
  });

  const dirty =
    JSON.stringify(bodyOf(draft)) !== JSON.stringify(bodyOf(draftOf(schedule))) || halfTyped;
  React.useEffect(() => onDirty(dirty), [dirty, onDirty]);

  const set = <K extends keyof IDraft>(member: K, value: IDraft[K]) =>
    setDraft((held) => ({ ...held, [member]: value }));

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (etag === null || busy) return;
    if (timeInput.current?.validity.badInput === true) {
      setHalfTyped(true);
      return;
    }
    setHalfTyped(false);
    setProblems([]);
    setRefusal(null);
    setConflict(false);
    setBusy(true);
    onSaving();
    try {
      onSaved(await brokerCheckRefreshApi.saveSchedule(bodyOf(draft), etag));
    } catch (error) {
      if (unanswered(error)) {
        onUnanswered();
        return;
      }
      const refused = refreshRefusal(error);
      if (refused?.kind === 'revision-conflict') {
        setConflict(true);
      } else if (refused?.kind === 'precondition-required') {
        setRefusal(
          'The schedule’s revision is needed, and this page did not send it — reload ' +
            'the page. Nothing was saved.'
        );
      } else if (error instanceof ApiError && error.status === 400 && error.problem?.problems?.length) {
        setProblems(error.problem.problems);
      } else {
        setRefusal(sentence(refusalMessage(error, 'The schedule was not saved')));
      }
    } finally {
      saved.current = true;
      setBusy(false);
    }
  };

  const reload = async () => {
    setReloading(true);
    const read = await onReload();
    handBack.current = read;
    setReloading(false);
    setConfirmingReload(false);
    if (!read) setRefusal('The schedule could not be read again — try Reload once more.');
  };

  const said = (member: Member) => problems.filter((problem) => memberOf(problem.target) === member);
  const weekly = draft.frequency === 'weekly';
  // A problem at no member, or at the week of the month while the form draws none, is said under
  // the form — never dropped for want of a field to say it beside.
  const unplaced = problems.filter((problem) => {
    const member = memberOf(problem.target);
    return member === null || (member === 'weekOfMonth' && weekly);
  });

  return (
    // `noValidate`: a time held half typed is invalid to the browser, which would block the
    // submit with its own bubble before the form could say why — and survey-api is the judge.
    <form aria-label="Schedule" noValidate onSubmit={save} className="grid gap-5 text-[0.8125rem]">
      <ScheduleField member="enabled" problems={said('enabled')}>
        <span className="flex flex-wrap items-center gap-2">
          <Checkbox
            id={`${id}-enabled`}
            checked={draft.enabled}
            onCheckedChange={(on) => set('enabled', on === true)}
          />
          <label htmlFor={`${id}-enabled`} className="font-medium">
            Enabled
          </label>
          <span className="text-muted-foreground">
            — while it is off, no slot starts a run and no paused run resumes
          </span>
        </span>
      </ScheduleField>

      <div className="grid gap-4 md:grid-cols-3">
        <ScheduleField member="frequency" problems={said('frequency')}>
          <label htmlFor={`${id}-frequency`} className="font-medium">
            Frequency
          </label>
          <CodeSelect
            id={`${id}-frequency`}
            value={draft.frequency}
            codes={FREQUENCIES}
            label={frequencyLabel}
            onChange={(value) => set('frequency', value)}
          />
        </ScheduleField>

        {!weekly && (
          <ScheduleField member="weekOfMonth" problems={said('weekOfMonth')}>
            <label htmlFor={`${id}-week`} className="font-medium">
              Week of the month
            </label>
            <CodeSelect
              id={`${id}-week`}
              value={draft.weekOfMonth}
              codes={WEEKS_OF_MONTH}
              label={weekOfMonthLabel}
              placeholder="Choose a week"
              onChange={(value) => set('weekOfMonth', value)}
            />
          </ScheduleField>
        )}

        <ScheduleField member="dayOfWeek" problems={said('dayOfWeek')}>
          <label htmlFor={`${id}-day`} className="font-medium">
            Day
          </label>
          <CodeSelect
            id={`${id}-day`}
            value={draft.dayOfWeek}
            codes={DAYS_OF_WEEK}
            label={dayOfWeekLabel}
            onChange={(value) => set('dayOfWeek', value)}
          />
        </ScheduleField>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <ScheduleField
          member="startLocal"
          problems={said('startLocal')}
          held={halfTyped ? 'Enter a whole time — nothing was sent.' : null}
        >
          <label htmlFor={`${id}-start`} className="font-medium">
            Start
          </label>
          <Input
            ref={timeInput}
            id={`${id}-start`}
            type="time"
            step={60}
            value={draft.startLocal}
            onChange={(event) => {
              set('startLocal', event.target.value);
              if (!event.target.validity.badInput) setHalfTyped(false);
            }}
            className="w-36"
          />
          <span className="text-xs text-muted-foreground">In the schedule’s time zone.</span>
        </ScheduleField>

        <ScheduleField member="timeZone" problems={said('timeZone')}>
          <label htmlFor={`${id}-zone`} className="font-medium">
            Time zone
          </label>
          <Input
            id={`${id}-zone`}
            list={`${id}-zones`}
            value={draft.timeZone}
            onChange={(event) => set('timeZone', event.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
          <datalist id={`${id}-zones`}>
            {browserZones().map((zone) => (
              <option key={zone} value={zone} />
            ))}
          </datalist>
          <span className="text-xs text-muted-foreground">
            An IANA id, such as America/New_York — an unknown one is refused.
          </span>
        </ScheduleField>

        <ScheduleField member="windowHours" problems={said('windowHours')}>
          <label htmlFor={`${id}-window`} className="font-medium">
            Window
          </label>
          <span className="flex items-center gap-2">
            <Input
              id={`${id}-window`}
              inputMode="numeric"
              value={draft.windowHours}
              onChange={(event) => set('windowHours', event.target.value)}
              autoComplete="off"
              className="w-24"
            />
            <span className="text-muted-foreground">hours</span>
          </span>
          <span className="text-xs text-muted-foreground">1–72, from the slot’s start.</span>
        </ScheduleField>
      </div>

      {unplaced.length > 0 && (
        <div role="alert">
          <ul aria-label="Refused" className="grid gap-1 text-destructive">
            {unplaced.map((problem, index) => (
              <li key={index}>
                <code className="font-mono">{problem.target}</code>: {problem.reason}
              </li>
            ))}
          </ul>
        </div>
      )}
      {conflict && (
        <Alert variant="destructive">
          <AlertTitle>Another admin saved the schedule since you opened it</AlertTitle>
          <AlertDescription>
            <span>
              Nothing was saved, and your changes are still in the form. Reload to see the
              schedule as it is saved now.
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-2"
              disabled={reloading}
              onClick={() => (dirty ? setConfirmingReload(true) : void reload())}
            >
              {reloading ? 'Reading…' : 'Reload'}
            </Button>
          </AlertDescription>
        </Alert>
      )}
      {refusal && (
        <Alert variant="destructive">
          <AlertDescription>{refusal}</AlertDescription>
        </Alert>
      )}
      {etag === null && (
        <p className="text-muted-foreground">
          The schedule came without its revision, so it cannot be saved here — reload the
          page.
        </p>
      )}

      <div>
        <Button
          id={`${id}-save`}
          type="submit"
          size="sm"
          disabled={busy || reloading || etag === null}
        >
          {busy ? 'Saving…' : 'Save'}
        </Button>
      </div>

      <AlertDialog
        open={confirmingReload}
        onOpenChange={(open) => !reloading && setConfirmingReload(open)}
      >
        <AlertDialogContent
          onCloseAutoFocus={(event) => {
            // Its trigger went with the conflict: the effect above places the focus.
            if (handBack.current) event.preventDefault();
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Drop your changes?</AlertDialogTitle>
            <AlertDialogDescription>
              Reload reads the schedule as it is saved now, and the form shows it — what you
              changed here is dropped.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reloading}>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              disabled={reloading}
              onClick={(event) => {
                // Held open until the read is in, as the hub's confirmations are.
                event.preventDefault();
                void reload();
              }}
            >
              {reloading ? 'Reading…' : 'Reload'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
}

/** One member's wrapper: its label and control, then what survey-api said of it, or the form's
 *  own sentence for a time held half typed. */
function ScheduleField({
  member,
  problems,
  held = null,
  children
}: {
  member: Member;
  problems: IProblem[];
  held?: Nullable<string>;
  children: React.ReactNode;
}) {
  return (
    <div data-schedule-field={member} className="grid content-start gap-1">
      {children}
      {(held !== null || problems.length > 0) && (
        <div role="alert" className="grid gap-0.5 text-destructive">
          {held !== null && <p>{held}</p>}
          {problems.map((problem, index) => (
            <p key={index}>{sentence(problem.reason)}</p>
          ))}
        </div>
      )}
    </div>
  );
}

/** A vocabulary's choices in words, a stored code this app lacks kept and spelled. */
function CodeSelect({
  id,
  value,
  codes,
  label,
  placeholder,
  onChange
}: {
  id: string;
  value: string;
  codes: readonly string[];
  label: (code: string) => string;
  placeholder?: string;
  onChange: (value: string) => void;
}) {
  const offered = withUnknown(codes, value === '' ? [] : [value]);
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id} className="w-44 max-w-full">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {offered.map((code) => (
          <SelectItem key={code} value={code}>
            {label(code)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
